package uz.mint.frontend.i18n

import uz.mint.frontend.SiteConfig

/**
 * The public offer and privacy policy.
 *
 * **Russian only, on every locale — deliberately.** The document titles are translated into UZ and
 * EN but not the bodies: these are operative legal texts, and a machine translation of refund
 * terms or a data-protection clause can be materially wrong in a way marketing copy cannot. UZ and
 * EN versions need a lawyer, not this file.
 *
 * **Approved by the client 2026-08-04**, which is what made them publishable. Until then the
 * footer named both documents without linking them, because the previous drafts carried a
 * fabricated taxpayer number from the design and promised automatic refunds the code has never
 * had. Both are now settled:
 *
 *  - Company particulars are the real ones, supplied by the owner: ИНН 312332545, the Yakkasaroy
 *    address, the bank account, the director.
 *  - Refunds are **on request within 24–48 hours**, the client's own wording. That is honest: the
 *    money moves as fast as Click or Payme returns it, and nothing in the code refunds by itself.
 *  - Analytics **is planned** ("да, будем делать"), so the cookie clause says so in advance rather
 *    than being rewritten the day a counter is added.
 *
 * `maxsustrash@gmail.com` is a placeholder the client accepted knowingly ("поменяем потом"). It is
 * a real, working address, which is what the law requires; swap it for one on the domain when
 * there is one.
 */
data class LegalDocument(
    val revision: String,
    val intro: String,
    val sections: List<Section>,
) {
    data class Section(val heading: String, val body: String)
}

private const val COMPANY = "ООО «MAXSUS TRASH»"
private const val EMAIL = "maxsustrash@gmail.com"

/**
 * The offer and the privacy policy quote the support line, and so does the strip above the nav.
 * One constant, in [SiteConfig], because a number that differs between the header and the legal
 * text is the kind of contradiction a customer notices and a court would too.
 */
private val PHONE = SiteConfig.PHONE

val PublicOffer = LegalDocument(
    revision = "Редакция от 4 августа 2026",
    intro = "Настоящий документ является публичной офертой $COMPANY (далее — Исполнитель) и содержит " +
        "все существенные условия оказания услуг по предоставлению электронных SIM-карт (eSIM) через " +
        "сайт mintaccess.uz. Совершение покупателем действий по оформлению и оплате заказа признаётся " +
        "полным и безоговорочным принятием этих условий.",
    sections = listOf(
        LegalDocument.Section(
            "1. Общие положения",
            "Оплата заказа означает заключение договора на условиях настоящей оферты. Исполнитель вправе " +
                "изменять её условия, публикуя новую редакцию на этой странице; к уже оплаченным заказам " +
                "применяется редакция, действовавшая на момент оплаты. Исполнитель не является оператором " +
                "сотовой связи: услуги связи оказывают операторы соответствующих стран, а Исполнитель " +
                "предоставляет доступ к профилю eSIM и информационную поддержку.",
        ),
        LegalDocument.Section(
            "2. Предмет договора",
            "Исполнитель предоставляет доступ к услугам передачи данных партнёрских сетей в объёме, на срок " +
                "и на территории, указанных в выбранном тарифе. Голосовые вызовы и SMS в пакет не входят, " +
                "если это прямо не указано в описании тарифа.",
        ),
        LegalDocument.Section(
            "3. Оплата",
            "Цены указаны в сумах Республики Узбекистан и включают все применимые налоги. Стоимость заказа " +
                "фиксируется в момент оформления: последующее изменение цен или курса валют на оформленный " +
                "заказ не влияет. Оплата принимается через платёжные системы Click и Payme. Обязательство " +
                "по оплате считается исполненным с момента поступления подтверждения от платёжной системы.",
        ),
        LegalDocument.Section(
            "4. Получение eSIM",
            "После подтверждения оплаты профиль eSIM размещается в личном кабинете покупателя в разделе " +
                "«Мои eSIM»: QR-код для установки, код активации в формате LPA, объём трафика, срок действия " +
                "и территория покрытия. Срок предоставления — до 30 минут с момента подтверждения оплаты. " +
                "Услуга считается оказанной с момента размещения профиля в личном кабинете.",
        ),
        LegalDocument.Section(
            "5. Активация и срок действия",
            "Срок действия пакета исчисляется с момента активации профиля — первого подключения к сети на " +
                "территории действия тарифа, — а не с момента покупки, если иное не указано в описании " +
                "тарифа. Неиспользованный трафик по истечении срока не переносится и не компенсируется.",
        ),
        LegalDocument.Section(
            "6. Возврат средств",
            "Возврат оформляется по запросу покупателя, направленному по контактам из раздела 8 с указанием " +
                "номера заказа. Средства возвращаются в течение 24–48 часов — срок зависит от того, сколько " +
                "времени займёт операция на стороне Click или Payme, — тем же способом, которым была " +
                "произведена оплата.\n\n" +
                "Возврат возможен, если профиль eSIM не был активирован. После активации возврат не " +
                "производится: услуга считается оказанной, а ресурс оператора связи израсходованным. Если " +
                "профиль не был предоставлен по вине Исполнителя, средства возвращаются в полном объёме.\n\n" +
                "Отсутствие покрытия в конкретной точке, ограничения скорости со стороны оператора связи и " +
                "несовместимость устройства покупателя с технологией eSIM основанием для возврата не " +
                "являются: сведения о требованиях к устройству и о территории покрытия доступны до покупки.",
        ),
        LegalDocument.Section(
            "7. Ответственность",
            "Исполнитель не отвечает за качество сигнала партнёрских сетей, скорость передачи данных и зону " +
                "покрытия, за несовместимость или блокировку устройства покупателя оператором (SIM-lock), " +
                "а также за ограничение услуги регулятором страны пребывания. Ответственность Исполнителя " +
                "во всех случаях ограничена стоимостью оплаченного тарифа. Совместимость устройства " +
                "покупатель проверяет до оплаты. Споры разрешаются по законодательству Республики " +
                "Узбекистан после претензионного порядка; срок ответа на претензию — 10 рабочих дней.",
        ),
        LegalDocument.Section(
            "8. Реквизиты Исполнителя",
            "$COMPANY (“MAXSUS TRASH” MCHJ)\n" +
                "Адрес: г. Ташкент, Яккасарайский район, ул. Кичик Бешёгоч, дом 25\n" +
                "ИНН: 312332545 · ОКЭД: 38110\n" +
                "Р/с: 2020800020728593700 в Шайхантахурском филиале АИБ «IPAK YO‘LI»\n" +
                "МФО: 01145 · ИНН банка: 207249969\n" +
                "Директор: Хасанов Абубакир\n" +
                "Телефон: $PHONE · E-mail: $EMAIL · Telegram: @mintesim",
        ),
    ),
)

val PrivacyPolicy = LegalDocument(
    revision = "Редакция от 4 августа 2026",
    intro = "Оператор персональных данных — $COMPANY. Мы собираем минимум данных — только то, без чего " +
        "нельзя выдать eSIM и подтвердить оплату. Обработка ведётся в соответствии с Законом Республики " +
        "Узбекистан «О персональных данных».",
    sections = listOf(
        LegalDocument.Section(
            "1. Какие данные мы обрабатываем",
            "Номер мобильного телефона — он же логин; имя и адрес электронной почты, если вы указали их " +
                "в профиле по собственному желанию; сведения о заказах: тариф, сумма и дата платежа, " +
                "идентификатор платёжной операции, идентификатор выданного профиля eSIM (ICCID); " +
                "технические данные — IP-адрес, тип браузера, посещённые страницы и время посещения.\n\n" +
                "Мы не собираем и не храним данные банковских карт: их реквизиты вводятся на стороне " +
                "Click и Payme и Исполнителю не передаются.",
        ),
        LegalDocument.Section(
            "2. Зачем они нужны",
            "Чтобы зарегистрировать вход по номеру телефона, оформить заказ и выдать профиль eSIM, " +
                "сообщить о статусе заказа, ответить в поддержку и вернуть средства при отмене, а также " +
                "чтобы исполнить требования законодательства о бухгалтерском и налоговом учёте и " +
                "предотвратить мошенничество. Мы не продаём данные и не передаём их для рекламы третьих лиц.",
        ),
        LegalDocument.Section(
            "3. Кому передаются",
            "Только в объёме, необходимом для оказания услуги: поставщику профилей eSIM — номер заказа и " +
                "код тарифа; платёжным системам Click и Payme — номер заказа и сумма; сервису SMS-рассылки — " +
                "номер телефона и текст с кодом подтверждения. Часть этих поставщиков находится за " +
                "пределами Республики Узбекистан. Иным третьим лицам данные не раскрываются, за исключением " +
                "случаев, предусмотренных законодательством.",
        ),
        LegalDocument.Section(
            "4. Cookie и аналитика",
            "Сайт использует технические cookie, необходимые для работы авторизации и защиты от подделки " +
                "запросов, а также для запоминания выбранного языка. Без них вход в личный кабинет " +
                "невозможен.\n\n" +
                "Дополнительно применяются обезличенные аналитические cookie — они помогают понять, какими " +
                "страницами пользуются и где посетители испытывают затруднения. Аналитика не связывает " +
                "просмотры с вашим именем или номером телефона. Cookie можно отключить в настройках " +
                "браузера; технические при этом отключатся тоже, и вход в личный кабинет перестанет работать.",
        ),
        LegalDocument.Section(
            "5. Хранение",
            "Данные личного кабинета хранятся до удаления учётной записи по вашему обращению. Данные о " +
                "заказах и платежах — в течение срока, установленного законодательством для документов " +
                "бухгалтерского учёта. Технические журналы — не более 12 месяцев.",
        ),
        LegalDocument.Section(
            "6. Ваши права",
            "Вы вправе получить сведения об обрабатываемых данных, потребовать их уточнения, блокирования " +
                "или удаления, отозвать согласие на обработку — при этом оказание услуг становится " +
                "невозможным, — а также обжаловать наши действия в уполномоченном государственном органе. " +
                "Обращение направляйте на $EMAIL или в Telegram @mintesim; отвечаем в течение " +
                "10 рабочих дней.",
        ),
        LegalDocument.Section(
            "7. Безопасность и контакты",
            "Доступ в личный кабинет защищён одноразовым кодом на номер телефона; пароли не используются " +
                "и не хранятся. Соединение с сайтом защищено протоколом TLS.\n\n" +
                "$COMPANY, г. Ташкент, Яккасарайский район, ул. Кичик Бешёгоч, дом 25. " +
                "ИНН 312332545. Телефон: $PHONE. E-mail: $EMAIL. Telegram: @mintesim.",
        ),
    ),
)
